import React, { FC, useMemo } from 'react';
import { useUser } from 'store/User/Context';
import styled, { keyframes } from 'styled-components';

const Wrapper = styled.div<{ fitParentContainer?: boolean; darkMode?: boolean }>`
    position: ${({ fitParentContainer }) => fitParentContainer ? 'absolute' : 'fixed'};
    top: 0;
    left: 0;
    height: ${({ fitParentContainer }) => fitParentContainer ? '100%' : '100vh'};
    width: ${({ fitParentContainer }) => fitParentContainer ? '100%' : '100vw'};
    background: rgba(${(props) => props.darkMode ? '0, 0, 0' : '255, 255, 255'}, 0.6);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 24px;
    padding: 20px;
`;

const ContentBox = styled.div<{ darkMode?: boolean }>`
    background: ${(props) => props.darkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.98)'};
    border-radius: 16px;
    padding: 32px 40px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    min-width: 320px;
    max-width: 420px;

    @media (max-width: 480px) {
        min-width: 280px;
        padding: 24px 20px;
    }
`;

const LogoWrapper = styled.div<{ darkMode?: boolean }>`
    position: relative;
    width: 64px;
    height: 64px;

    img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 56px;
        height: 56px;
        filter: ${(props) => props.darkMode
            ? 'brightness(0) invert(1)'
            : 'brightness(0) saturate(100%) invert(21%) sepia(95%) saturate(2285%) hue-rotate(201deg) brightness(97%) contrast(101%)'};
    }
`;

const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const SpinnerCircle = styled.div<{ darkMode?: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 3px solid transparent;
    border-top-color: ${(props) => props.darkMode ? '#FFFFFF' : '#0255aa'};
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
`;

const SegmentedProgressBar = styled.div`
    width: 100%;
    display: flex;
    gap: 4px;
`;

const progressPulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
`;

const ProgressSegment = styled.div<{ isCompleted: boolean; isActive: boolean; darkMode?: boolean }>`
    flex: 1;
    height: 8px;
    border-radius: 4px;
    background: ${(props) => {
        if (props.isCompleted) {
            return props.darkMode ? '#4a9eff' : '#0255aa';
        }

        if (props.isActive) {
            return props.darkMode ? '#4a9eff' : '#0255aa';
        }

        return props.darkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(2, 85, 170, 0.15)';
    }};
    transition: background 0.3s ease;
    animation: ${(props) => props.isActive ? progressPulse : 'none'} 1.5s ease-in-out infinite;
`;

const StatusText = styled.div<{ darkMode?: boolean }>`
    font-size: 16px;
    font-weight: 600;
    color: ${(props) => props.darkMode ? '#FFFFFF' : '#0255aa'};
    text-align: center;
`;

const StepLabel = styled.div<{ darkMode?: boolean }>`
    font-size: 13px;
    color: ${(props) => props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(2, 85, 170, 0.7)'};
    text-align: center;
`;

export interface ProgressStep {
    key: string;
    label: string;
}

interface ProgressIndicatorPropsType {
    fitParentContainer?: boolean;
    text?: string;
    currentStep?: string;
    steps?: ProgressStep[];
}

const ProgressIndicator: FC<ProgressIndicatorPropsType> = ({
    fitParentContainer,
    text,
    currentStep,
    steps = []
}) => {
    const { user } = useUser();
    const darkMode = user.isDarkMode;

    const { currentStepIndex, currentStepLabel } = useMemo(() => {
        const stepIndex = steps.findIndex((step) => step.key === currentStep);
        const activeIndex = stepIndex >= 0 ? stepIndex : 0;
        const label = steps[activeIndex]?.label || '';

        return {
            currentStepIndex: activeIndex,
            currentStepLabel: label
        };
    }, [currentStep, steps]);

    return (
        <Wrapper fitParentContainer={fitParentContainer} darkMode={darkMode}>
            <ContentBox darkMode={darkMode}>
                <LogoWrapper darkMode={darkMode}>
                    <img src="/logo_favicon.png" alt="Skaner logo" />
                    <SpinnerCircle darkMode={darkMode} />
                </LogoWrapper>

                {currentStep &&
                    <SegmentedProgressBar>
                        {steps.map((step, index) => <ProgressSegment
                            key={step.key}
                            isCompleted={index < currentStepIndex}
                            isActive={index === currentStepIndex}
                            darkMode={darkMode}
                        />)}
                    </SegmentedProgressBar>
                }

                {text && <StatusText darkMode={darkMode}>{text}</StatusText>}
                {currentStep && currentStepLabel &&
                    <StepLabel darkMode={darkMode}>
                        Krok {currentStepIndex + 1} z {steps.length}: {currentStepLabel}
                    </StepLabel>
                }
            </ContentBox>
        </Wrapper>
    );
};

export default ProgressIndicator;
